import { AppData, Log, Router } from "@lightningjs/sdk";
import { AppSetup } from "./app-setup";
import { Logger } from "./logger";
import { Helper } from "./helper";
import { GRIDTYPE, PLATFORM } from "./enum";
import AppModel from "./models/app-model";
import { AdHandler } from "./ad-handler";
import { getSiteConfig } from "./models/data-service";

/**
 * Globals that live on AppState/Config rather than on a per-site AppModel, so
 * they must be manually stashed when leaving an app and restored on return.
 * Everything else (siteId, appApiConfig, navigation stack, grids, ad state) is
 * encapsulated per-AppModel and is preserved automatically by the app stack.
 */
interface SavedGlobals {
    noVideosStarted: number;
    noVideosSinceLastBugAd: number;
    siteId: string;
    enableBugAds: boolean;
}

/**
 * ChildApp manages launching a different site ("child app") in-app and
 * returning to the previous one, mirroring the legacy ChildApp but built on the
 * app's existing `appState.appStack: AppModel[]`. Launching pushes a fresh
 * AppModel for the target site (which isolates that site's state); closing pops
 * back to the parent AppModel.
 */
class ChildApp {

    adHandler: AdHandler = new AdHandler();

    /** True when at least one child app is stacked on top of the main app. */
    get isChildAppLoaded(): boolean {
        const { appState } = AppData as AppSetup;
        return appState.appStack.length > 1;
    }

    /**
     * Launch a different site in-app. Optionally deep-links to a playId within
     * that site (openViaDeeplink); otherwise lands on the site's main grid.
    */
    loadChildApp = (siteInfo: { site_id: string }, playId: string | null = null) => {
        const { appConfig, appState, platform } = AppData as AppSetup;
        if (!appConfig || !appState) return;
        const parentApp = appState.getApp();
        if (!parentApp) return;

        const childSiteId = platform ? platform.remapSiteId(siteInfo.site_id) : siteInfo.site_id;

        Logger.processUserAction('InApp_newChildAppOpened', parentApp.siteId + ' to ' + childSiteId);

        this.showLoading(true);

        // Tell app-level widgets (menu etc.) the site changed so they rebuild
        // for the child - pages recreate per navigation, but widgets are boot-once.
        this.emitSiteChanged();

        (parentApp as any).savedGlobals = {
            noVideosStarted: appState.player.noVideosStarted,
            noVideosSinceLastBugAd: appState.player.noVideosSinceLastBugAd,
            siteId: appConfig.siteId,
            enableBugAds: appConfig.enableBugAds,
        } as SavedGlobals;

        // Reset global counters so the child app starts clean (mirrors legacy).
        appState.player.noVideosStarted = 0;
        appState.player.noVideosSinceLastBugAd = 0;
        appState.player.videoStartedFromResumePoint = 0;
        appState.videoMode = false;
        appState.clearDeeplink();

        // Repoint the global site id so ad macros and per-site parental keys
        // follow the child site. Restored in closeChildApp().
        appConfig.siteId = childSiteId;
        const rootConfig = (window as any)._rootConfig;
        if (rootConfig) rootConfig.siteId = childSiteId;

        appConfig.sessionId = Helper.generateSessionId();
        Log.info('CurrentApp: sessionId', appConfig.sessionId);

        // Optional deep-link target within the child app.
        if (playId) {
            appState.playlistPlayMode = true;
            appState.deeplinks.sourceType = 'hs-d';
            appState.deeplinks.type = 'shows';
            appState.deeplinks.playId = playId;
        }

        // Push a fresh AppModel for the child site. Its constructor sets
        // window.currentApp, and getApp() now returns this child.
        const childAppModel = new AppModel(childSiteId, false);
        appState.appStack.push(childAppModel);

        // Load the child site's index/config AND (re)decide the ad setup for the
        // child site + platform, then route into it.
        this.setupAdsForApp(childAppModel, true).then(() => {
            if (playId && appState.playlistPlayMode && !appState.deeplinks.isInvalidPattern) {
                appState.isDeeplinkInterupped = true;
                // Deeplink routes to the video screen (which shows its own video
                // loader); hide the switch overlay once that routing completes.
                Promise.resolve(childAppModel.deeplink.loadPlaylistAndPlay())
                    .finally(() => this.showLoading(false));
            } else {
                Logger.logSourceInfo();
                Router.navigate('main-grid-screen/0', false);
            }
        });
    }

    /** Show/hide the full-screen loading overlay (global LoadingScreen widget). */
    private showLoading = (show: boolean) => {
        try {
            const page: any = Router.getActivePage();
            page?.widgets?.loadingscreen?.showL1LoadingScreen(show);
        } catch (e) { /* loading widget not available */ }
    }

    /**
     * Signal the app root that the active site changed. Persistent app-level
     * widgets (menu, etc.) subscribe to this and rebuild their site-specific UI
     * for the new site - pages recreate per navigation, but widgets are built
     * once at boot. This keeps the switch decoupled from widget internals.
     */
    private emitSiteChanged = () => {
        try {
            const application: any = Router.getActivePage()?.application;
            application?.emit('siteChanged');
        } catch (e) {  }
    }

    /**
     * Reproduce splash's ad-SDK setup for the given app/site so ads are
     * configured for its siteId + platform on a runtime site switch. Mirrors
     * src/pages/splash.ts initializeApp - keep the two in sync. `doLoadIndex`
     * fetches the site index (child launch); when false the app's config is
     * already loaded (parent on return) and we just re-init the handler.
     */
    setupAdsForApp = async (app: AppModel, doLoadIndex: boolean) => {
        const { appConfig, appState, platform } = AppData as AppSetup;
        if (!platform) return;

        // 1. Start from the platform's preferred SDK so it can be re-promoted
        //    (not just narrowed) when switching between sites.
        appConfig.disableAds = false;
        
        const parentApiVersion = appConfig.apiVersion
        const parentEnableSecurityToken = appConfig.enableSecurityToken
        const parentSecurityToken = appConfig.securityToken

        Helper.getSiteConfigInfo(platform._siteConfig)
        Log.info('CurrentApp: siteConfig: ', platform._siteConfig)
        
        appConfig.apiVersion = parentApiVersion
        appConfig.enableSecurityToken = parentEnableSecurityToken
        appConfig.securityToken = parentSecurityToken

        appConfig.adSdkToUse = platform.getDefaultAdSdk();

        // 2. Device / platform narrowing (mirrors splash pre-loadIndex checks).
        if (!platform.getFullModel()) appConfig.adSdkToUse = 'ima';
        if (Helper.isOldDevice() == 1) appConfig.adSdkToUse = 'ima';
        if (appConfig.platform_id == PLATFORM.COX
            || appConfig.platform_id == PLATFORM.ROGERS
            || appConfig.platform_id == PLATFORM.SHAW) {
            appConfig.adSdkToUse = 'ima';
        }

        // 3. Load / (re)initialise this site's ad config. loadIndex() runs
        //    initAdHandler() which parses the site's adsdkConfig; on the parent's
        //    return the config is intact so we just re-init (idempotent).
        if (doLoadIndex) {
            await app.loadIndex();
        } else {
            // app.initAdHandler();
            this.adHandler.initializeAdsConfig();
        }

        // 4. Genre-based narrowing + coppa (site-scoped).
        const appApiConfig = appState.getAppApiConfig();
        let coppa = 'false';
        const genre = appApiConfig?.appConfig?.genre;
        if (genre && genre.toLocaleLowerCase() !== 'movies') {
            appConfig.adSdkToUse = 'ima';
            coppa = 'true';
        }

        // 5. FreeWheel validity check (only if still eligible).
        if (appConfig.adSdkToUse != 'ima') {
            try {
                const adConfig = await platform.getAdConfig({ environment: 'prod', authenticationEntity: 'MVPD', coppa });
                if (!(adConfig && (adConfig.adNetworkId || adConfig.adNetworkID))) {
                    appConfig.adSdkToUse = 'ima';
                }
            } catch (e) {
                appConfig.adSdkToUse = 'ima';
            }
        }

        // 6. disableAds guards (empty adsdklist + COPPA kids age-policy).
        if (appConfig.adSdkToUse == 'ima' && app.adObj && app.adObj.adsdklist && app.adObj.adsdklist.length == 0) {
            appConfig.disableAds = true;
        }
        if (platform.getAgePolicy() == 'app:child'
            && appApiConfig?.appConfig?.genre?.toLocaleLowerCase() !== 'kids'
            && Helper.hasKidsL1Menu(appApiConfig?.appConfig?.channels) !== -1) {
            appConfig.disableAds = true;
        }

        Logger.processUserAction('AdSetupFor_' + app.siteId, appConfig.adSdkToUse + '_disableAds:' + appConfig.disableAds);
    }

    /**
     * Close the current child app. `toMainApp` pops all the way back to the
     * main app; otherwise returns one level to the previous app.
     */
    closeChildApp = (toMainApp: boolean = false) => {
        const { appConfig, appState } = AppData as AppSetup;
        if (!appConfig || !appState) return;
        if (!this.isChildAppLoaded) return;

        // Show the loading overlay during the return switch; main-grid hides it
        // once the parent's grid rebuilds.
        this.showLoading(true);

        // Tell app-level widgets the site changed so the menu rebuilds for the parent.
        this.emitSiteChanged();

        if (toMainApp) {
            while (appState.appStack.length > 1) {
                this.popChild();
            }
        } else {
            this.popChild();
        }

        const parentApp = appState.getApp();
        if (!parentApp) return;
        (window as any).currentApp = parentApp;

        appConfig.siteId = parentApp.siteId;
        const rootConfig = (window as any)._rootConfig;
        if (rootConfig) rootConfig.siteId = parentApp.siteId;

        appConfig.sessionId = Helper.generateSessionId();

        const saved = (parentApp as any).savedGlobals as SavedGlobals | undefined;
        if (saved) {
            appState.player.noVideosStarted = saved.noVideosStarted;
            appState.player.noVideosSinceLastBugAd = saved.noVideosSinceLastBugAd;
            appConfig.enableBugAds = saved.enableBugAds;
            delete (parentApp as any).savedGlobals;
        }

        Logger.processUserAction('InApp_GoingBackTo_' + (toMainApp ? 'MainApp' : 'PreviousApp'), parentApp.siteId);

        appState.videoMode = false;
        appState.clearDeeplink();

        const topGrid = parentApp.navigation.getCurrentGrid();
        if (topGrid && topGrid.type === GRIDTYPE.DYNID) {
            parentApp.navigation.continuesStack.pop();
            if (parentApp.dynidGrid) {
                parentApp.dynidGrid.destroy();
                parentApp.dynidGrid = undefined;
            }
        }

        const l1 = parentApp.l1CategoryToLoad >= 0 ? parentApp.l1CategoryToLoad : 0;
        const currentGrid = parentApp.navigation.getCurrentGrid();
        this.setupAdsForApp(parentApp, false).then(() => {
            if (currentGrid && currentGrid.isMlp && currentGrid.mlpId) {
                Router.navigate('mlp-grid-screen/' + currentGrid.mlpId, false);
            } else {
                Router.navigate('main-grid-screen/' + l1 + '_' + new Date().getTime(), false);
            }
        });
    }

    /** Pop a single child AppModel off the stack and free its grid + ad state. */
    private popChild = () => {
        const { appState } = AppData as AppSetup;
        if (appState.appStack.length <= 1) return;
        const child = appState.appStack.pop();
        try {
            // Tear down any in-flight ad instances bound to the shared ad DOM
            // before discarding the child AppModel.
            child?.imaAdHandler?.resetIma();
            const fw: any = child?.freeWheelAds;
            if (fw && fw.player) {
                fw.breakPosition = 0;
                fw.player.onContentVideoEnded();
            }
            child?.navigation?.reset();
        } catch (e) { /* best-effort teardown */ }
    }
}

export default new ChildApp();
